import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Upload, FolderOpen, CheckCircle2, XCircle, Loader2, FileText, AlertTriangle, ArrowRight, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

const CATEGORY_OPTIONS = [
  { value: "revenue", label: "Ricavi" },
  { value: "variable_cost", label: "Costo Variabile" },
  { value: "fixed_cost", label: "Costo Fisso" },
  { value: "depreciation", label: "Ammortamento" },
  { value: "financial", label: "Onere Finanziario" },
  { value: "tax", label: "Imposta" },
  { value: "admin_compensation", label: "Compenso Amministratore" },
  { value: "exclude", label: "Escludi dal CE" },
];

const SUBCATEGORY_MAP = {
  fixed_cost: [
    { value: "personnel", label: "Personale" },
    { value: "rent", label: "Affitto" },
    { value: "leasing", label: "Leasing" },
    { value: "equipment", label: "Attrezzature" },
    { value: "maintenance", label: "Manutenzione" },
    { value: "utilities", label: "Utenze" },
    { value: "admin", label: "Amministrazione" },
    { value: "marketing", label: "Marketing" },
    { value: "consulting", label: "Consulenze" },
    { value: "travel", label: "Viaggi" },
    { value: "insurance", label: "Assicurazioni" },
    { value: "security", label: "Sicurezza" },
    { value: "other", label: "Altro" },
  ],
  variable_cost: [
    { value: "materials", label: "Materiali" },
    { value: "consulting", label: "Consulenze" },
    { value: "other", label: "Altro" },
  ],
};

export const BulkUploadDialog = ({ clientId, clientName, open, onOpenChange, onComplete }) => {
  const [year, setYear] = useState("2025");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [step, setStep] = useState(1); // 1=upload, 2=classify, 3=verify
  const [unclassified, setUnclassified] = useState([]);
  const [classifications, setClassifications] = useState({});
  const [saving, setSaving] = useState(false);
  const [verification, setVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, filename: '' });
  const inputRef = useRef(null);

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || []).filter(
      f => f.name.toLowerCase().endsWith('.pdf') || f.name.toLowerCase().endsWith('.csv') || f.name.toLowerCase().endsWith('.xlsx') || f.name.toLowerCase().endsWith('.xls')
    );
    setFiles(selected);
    setResults(null);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    setResults(null);
    setProgress({ current: 0, total: files.length, filename: files[0]?.name || '' });

    const aggregated = {
      success_count: 0,
      error_count: 0,
      results: [],
    };

    try {
      // Upload sequenziale: un file alla volta per evitare timeout su CDN/proxy in prod.
      // Ogni singolo bulk-upload con 1 PDF è veloce (~5-15s), mentre 12 insieme superano 2 min.
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setProgress({ current: i + 1, total: files.length, filename: f.name });
        const fd = new FormData();
        fd.append('files', f);
        try {
          const res = await axios.post(
            `${API}/clients/${clientId}/bulk-upload?year=${year}`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 }
          );
          aggregated.success_count += res.data.success_count || 0;
          aggregated.error_count += res.data.error_count || 0;
          (res.data.results || []).forEach(r => aggregated.results.push(r));
        } catch (e) {
          aggregated.error_count += 1;
          aggregated.results.push({
            filename: f.name,
            status: 'error',
            error: e.response?.data?.detail || e.message,
          });
        }
      }

      setResults(aggregated);
      if (aggregated.success_count > 0) {
        toast.success(`${aggregated.success_count} mesi importati per il ${year}`);
        await checkUnclassified();
      }
      if (aggregated.error_count > 0) {
        toast.error(`${aggregated.error_count} file con errori`);
      }
    } catch (e) {
      toast.error("Errore nel caricamento: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      setProgress({ current: 0, total: 0, filename: '' });
    }
  };

  const checkUnclassified = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/unclassified-accounts`);
      const accounts = res.data.accounts || [];
      setUnclassified(accounts);
      if (accounts.length > 0) {
        setStep(2);
        // Pre-fill classifications
        const init = {};
        accounts.forEach(a => {
          init[a.account_code] = { category: a.suggested_category || "", subcategory: a.suggested_subcategory || "" };
        });
        setClassifications(init);
      } else {
        // No unclassified, go to verification
        await runVerification();
      }
    } catch {
      // Endpoint might not exist yet, skip
      await runVerification();
    }
  }, [clientId]);

  const saveClassifications = async () => {
    setSaving(true);
    try {
      const mappings = Object.entries(classifications)
        .filter(([, v]) => v.category)
        .map(([code, v]) => ({
          account_code: code,
          account_name: unclassified.find(a => a.account_code === code)?.account_name || "",
          category: v.category,
          subcategory: v.subcategory || null,
        }));

      if (mappings.length > 0) {
        await axios.post(`${API}/clients/${clientId}/account-mappings/bulk`, { mappings });
        // Recalculate all periods
        await axios.post(`${API}/clients/${clientId}/recalculate`);
        toast.success(`${mappings.length} conti classificati e periodi ricalcolati`);
      }
      await runVerification();
    } catch (e) {
      toast.error("Errore nel salvataggio: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const runVerification = async () => {
    setVerifying(true);
    setStep(3);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/ce-verification?year=${year}`);
      setVerification(res.data);
    } catch (e) {
      setVerification({ error: e.response?.data?.detail || "Errore nella verifica" });
    } finally {
      setVerifying(false);
    }
  };

  const handleClose = () => {
    if (results?.success_count > 0 || step > 1) onComplete?.();
    setFiles([]);
    setResults(null);
    setStep(1);
    setUnclassified([]);
    setClassifications({});
    setVerification(null);
    onOpenChange(false);
  };

  const setCategory = (code, category) => {
    setClassifications(prev => ({ ...prev, [code]: { ...prev[code], category, subcategory: "" } }));
  };

  const setSubcategory = (code, subcategory) => {
    setClassifications(prev => ({ ...prev, [code]: { ...prev[code], subcategory } }));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 1 && <><FolderOpen className="h-5 w-5" /> Carica Bilancio - {clientName}</>}
            {step === 2 && <><AlertTriangle className="h-5 w-5 text-amber-500" /> Classifica Conti - {clientName}</>}
            {step === 3 && <><ShieldCheck className="h-5 w-5 text-emerald-500" /> Verifica CE - {clientName}</>}
          </DialogTitle>
          {/* Step indicator */}
          <div className="flex items-center gap-2 pt-2">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center gap-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  step === s ? "bg-blue-600 text-white" : step > s ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
                }`}>{step > s ? "✓" : s}</div>
                <span className={`text-xs ${step === s ? "font-semibold" : "text-muted-foreground"}`}>
                  {s === 1 ? "Carica" : s === 2 ? "Classifica" : "Verifica"}
                </span>
                {s < 3 && <ArrowRight className="h-3 w-3 text-slate-400 mx-1" />}
              </div>
            ))}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* STEP 1: Upload */}
          {step === 1 && (
            <>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm space-y-2">
                <div className="font-semibold text-blue-800">Come nominare i file</div>
                <div className="text-blue-700">
                  Nomina i PDF con il <strong>numero del mese</strong> all'inizio:
                </div>
                <div className="grid grid-cols-3 gap-1 text-xs text-blue-600 font-mono mt-1">
                  <span>01.pdf = Gennaio</span><span>02.pdf = Febbraio</span><span>03.pdf = Marzo</span>
                  <span>04.pdf = Aprile</span><span>05.pdf = Maggio</span><span>06.pdf = Giugno</span>
                  <span>07.pdf = Luglio</span><span>08.pdf = Agosto</span><span>09.pdf = Settembre</span>
                  <span>10.pdf = Ottobre</span><span>11.pdf = Novembre</span><span>12.pdf = Dicembre</span>
                </div>
              </div>

              <div className="flex items-end gap-4">
                <div>
                  <Label className="text-sm">Anno</Label>
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger className="w-24" data-testid="bulk-year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2023, 2024, 2025, 2026].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <input ref={inputRef} type="file" multiple accept=".pdf,.csv,.xlsx,.xls" onChange={handleFiles} className="hidden" data-testid="bulk-file-input" />
                  <Button variant="outline" className="w-full h-20 border-dashed border-2" onClick={() => inputRef.current?.click()} data-testid="bulk-select-files-btn">
                    <div className="flex flex-col items-center gap-1">
                      <Upload className="h-6 w-6 text-muted-foreground" />
                      <span className="text-sm">{files.length > 0 ? `${files.length} file selezionati` : "Seleziona i PDF mensili"}</span>
                    </div>
                  </Button>
                </div>
              </div>

              {files.length > 0 && !results && (
                <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto">
                  {files.sort((a, b) => a.name.localeCompare(b.name)).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm p-1.5 bg-slate-50 rounded">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{f.name}</span>
                    </div>
                  ))}
                </div>
              )}

              {results && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <Badge className="bg-emerald-100 text-emerald-700">{results.success_count} importati</Badge>
                    {results.error_count > 0 && <Badge className="bg-red-100 text-red-700">{results.error_count} errori</Badge>}
                  </div>
                  {results.imported?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead><tr className="bg-emerald-50 border-b text-xs">
                          <th className="text-left px-3 py-1.5">Mese</th><th className="text-left px-3 py-1.5">File</th>
                          <th className="text-right px-3 py-1.5">Voci</th><th className="text-right px-3 py-1.5">Ricavi</th><th className="text-right px-3 py-1.5">EBITDA</th>
                        </tr></thead>
                        <tbody>
                          {results.imported.map((r, i) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="px-3 py-1.5 font-medium"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 inline mr-1" />{r.month_name} {year}</td>
                              <td className="px-3 py-1.5 text-muted-foreground text-xs truncate max-w-[150px]">{r.filename}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{r.entries_count}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{fmtEur(r.revenue)}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{fmtEur(r.ebitda)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {results.errors?.length > 0 && results.errors.map((e, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm p-2 bg-red-50 border border-red-200 rounded">
                      <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">{e.filename}</span> - <span className="text-red-600">{e.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* STEP 2: Classify unclassified accounts */}
          {step === 2 && (
            <>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <div className="font-semibold text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> {unclassified.length} conti da classificare
                </div>
                <div className="text-amber-700 mt-1">
                  Classifica ogni conto per avere un CE corretto. I periodi verranno ricalcolati automaticamente.
                </div>
              </div>

              <div className="max-h-[50vh] overflow-y-auto space-y-2">
                {unclassified.map(acc => {
                  const cat = classifications[acc.account_code]?.category || "";
                  const subOpts = SUBCATEGORY_MAP[cat] || [];
                  return (
                    <div key={acc.account_code} className="p-3 border rounded-lg bg-white space-y-2" data-testid={`classify-${acc.account_code}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-mono text-xs text-muted-foreground">{acc.account_code}</span>
                          <span className="font-semibold ml-2">{acc.account_name}</span>
                        </div>
                        <span className="text-sm font-semibold tabular-nums">{fmtEur(acc.total_amount)}</span>
                      </div>
                      <div className="flex gap-2">
                        <Select value={cat} onValueChange={(v) => setCategory(acc.account_code, v)}>
                          <SelectTrigger className="w-48 h-8 text-xs"><SelectValue placeholder="Categoria..." /></SelectTrigger>
                          <SelectContent>
                            {CATEGORY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {subOpts.length > 0 && (
                          <Select value={classifications[acc.account_code]?.subcategory || ""} onValueChange={(v) => setSubcategory(acc.account_code, v)}>
                            <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Sotto-cat..." /></SelectTrigger>
                            <SelectContent>
                              {subOpts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* STEP 3: Verification */}
          {step === 3 && (
            <>
              {verifying ? (
                <div className="py-12 text-center">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-500 mb-3" />
                  <p className="text-muted-foreground">Verifica in corso...</p>
                </div>
              ) : verification?.error ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{verification.error}</div>
              ) : verification ? (
                <div className="space-y-4">
                  {/* Overall status */}
                  <div className={`p-4 rounded-lg border ${verification.all_ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                    <div className={`font-bold flex items-center gap-2 ${verification.all_ok ? "text-emerald-800" : "text-red-800"}`}>
                      {verification.all_ok ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                      {verification.all_ok ? "CE corretto: i totali corrispondono" : "Attenzione: ci sono discrepanze"}
                    </div>
                    {!verification.all_ok && (
                      <p className="text-sm text-red-700 mt-1">Alcuni totali del CE non corrispondono al bilancio importato. Verifica la classificazione dei conti.</p>
                    )}
                  </div>

                  {/* Period-by-period comparison */}
                  {verification.periods?.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Mese</TableHead>
                          <TableHead className="text-right">Ricavi Bilancio</TableHead>
                          <TableHead className="text-right">Ricavi CE App</TableHead>
                          <TableHead className="text-right">Costi Bilancio</TableHead>
                          <TableHead className="text-right">Costi CE App</TableHead>
                          <TableHead className="text-right">Risultato Bil.</TableHead>
                          <TableHead className="text-right">Risultato CE</TableHead>
                          <TableHead className="text-center">Stato</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {verification.periods.map((p, i) => (
                          <TableRow key={i} className={p.ok ? "" : "bg-red-50"}>
                            <TableCell className="font-medium">{p.month_name} {p.year}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtEur(p.bilancio_revenue)}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtEur(p.ce_revenue)}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtEur(p.bilancio_costs)}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtEur(p.ce_costs)}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">{fmtEur(p.bilancio_result)}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">{fmtEur(p.ce_result)}</TableCell>
                            <TableCell className="text-center">
                              {p.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500 inline" /> : <XCircle className="h-4 w-4 text-red-500 inline" />}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                  {/* Unclassified accounts remaining */}
                  {verification.unclassified_count > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                      <span className="font-semibold text-amber-800">{verification.unclassified_count} conti ancora non classificati</span>
                      <span className="text-amber-700 ml-1">— torna a "Configura Conti" per classificarli</span>
                    </div>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>

        <DialogFooter>
          {step === 1 && !results && uploading && progress.total > 0 && (
            <div className="w-full bg-blue-50 border border-blue-200 rounded-md p-3 mb-2" data-testid="bulk-upload-progress">
              <div className="flex justify-between items-center mb-1.5 text-xs">
                <span className="font-semibold text-blue-900">
                  Caricamento in corso: {progress.current}/{progress.total}
                </span>
                <span className="text-blue-700 tabular-nums">
                  {Math.round((progress.current / progress.total) * 100)}%
                </span>
              </div>
              <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <div className="text-[11px] text-slate-600 mt-1 truncate" title={progress.filename}>
                <FileText className="h-3 w-3 inline mr-1" />{progress.filename}
              </div>
            </div>
          )}
          {step === 1 && !results && (
            <>
              <Button variant="outline" onClick={handleClose} disabled={uploading}>Annulla</Button>
              <Button onClick={handleUpload} disabled={files.length === 0 || uploading} data-testid="bulk-upload-btn">
                {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {progress.current}/{progress.total}...</> : <><Upload className="h-4 w-4 mr-1" /> Carica {files.length} file per {year}</>}
              </Button>
            </>
          )}
          {step === 1 && results && (
            <>
              <Button variant="outline" onClick={handleClose}>Chiudi</Button>
              <Button onClick={checkUnclassified} data-testid="proceed-classify-btn">
                <ArrowRight className="h-4 w-4 mr-1" /> Verifica classificazione
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => runVerification()}>Salta classificazione</Button>
              <Button onClick={saveClassifications} disabled={saving} data-testid="save-classifications-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvataggio...</> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Salva e verifica CE</>}
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              {!verification?.all_ok && verification && !verification.error && (
                <Button variant="outline" onClick={() => { setStep(2); checkUnclassified(); }}>Torna a classificare</Button>
              )}
              <Button onClick={handleClose} data-testid="bulk-close-btn">
                {verification?.all_ok ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Tutto OK - Chiudi</> : "Chiudi"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadDialog;
